/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'


export type AppState = 
  | 'initializing'        
  | 'loading-for-page'    
  | 'loading-for-panel'   
  | 'ready'              
  | 'panel-transitioning' 
  | 'panel-open'          

interface PanelReadiness {
  design: {
    dataLoaded: boolean
    contentReady: boolean
  }
  technology: {
    dataLoaded: boolean
    contentReady: boolean
  }
}

export const useAppStateStore = defineStore('appState', () => {
  // Core state machine
  const currentState = ref<AppState>('initializing')
  const previousState = ref<AppState | null>(null)
  
  // Loading coordination
  const coreDataLoaded = ref(false)
  const animationCompleted = ref(false)
  const isInitialLoad = ref(true)
  
  // Panel state tracking
  const activePanelSide = ref<'left' | 'right' | null>(null)
  const pendingPanelEntrance = ref<'left' | 'right' | null>(null)
  const panelReadiness = ref<PanelReadiness>({
    design: { dataLoaded: false, contentReady: false },
    technology: { dataLoaded: false, contentReady: false }
  })
  

  const shouldDelayPanelEntrance = ref(false)
  const panelEntranceGracePeriod = ref(500)
  
  // Animation coordination states
  const animationStates = ref({
    headerEntering: false,
    footerEntering: false,
    loadingHidden: false
  })
  
  // Animation timing constants
  const ANIMATION_TIMING = {
    LOGO_COMPLETE_DELAY: 300,
    HEADER_ENTRANCE_DELAY: 600,
    FOOTER_ENTRANCE_DELAY: 1200,
    PANEL_TRANSITION_DURATION: 300,
    PANEL_SWITCH_DURATION: 400,
    INITIAL_LOAD_DELAY: 1000,
    GRACE_PERIOD: 500
  } as const
  
  // Route utilities (shared between App.vue and usePanels.ts)
  const RouteUtils = {
    isPanelRoute: (path: string) => path.startsWith('/design') || path.startsWith('/technology'),
    
    getPanelSideFromRoute: (path: string): 'left' | 'right' | null => {
      if (path.startsWith('/design')) return 'left'
      if (path.startsWith('/technology')) return 'right'
      return null
    },
    
    isRegularRouteTransition: (newPath: string, oldPath: string) => {
      const isNewPanel = RouteUtils.isPanelRoute(newPath)
      const isOldPanel = RouteUtils.isPanelRoute(oldPath)
      return newPath !== oldPath && !isNewPanel && !isOldPanel
    },
    
    shouldScrollToTop: (newPath: string, oldPath: string) => {
      return newPath !== oldPath && !RouteUtils.isPanelRoute(newPath)
    }
  }
  
  // Computed properties
  const isReady = computed(() => 
    coreDataLoaded.value && animationCompleted.value
  )
  
  const canEnterPanel = computed(() => 
    currentState.value === 'ready' && isReady.value
  )
  

  
  // State machine transitions
  function transitionTo(newState: AppState, reason?: string) {
    previousState.value = currentState.value
    currentState.value = newState
  }
  
  function setCoreDataLoaded(loaded: boolean) {
    coreDataLoaded.value = loaded
    if (loaded && animationCompleted.value) {
      checkReadinessAndTransition()
    }
  }
  
  function setAnimationCompleted(completed: boolean) {
    animationCompleted.value = completed
    if (completed && coreDataLoaded.value) {
      checkReadinessAndTransition()
    }
  }
  
  // Panel data coordination
  function setPanelDataLoaded(side: 'left' | 'right', loaded: boolean) {
    const panelKey = side === 'left' ? 'design' : 'technology'
    panelReadiness.value[panelKey].dataLoaded = loaded
    
    if (pendingPanelEntrance.value === side && loaded) {
      handlePanelDataReady(side)
    }
  }
  
  function setPanelContentReady(side: 'left' | 'right', ready: boolean) {
    const panelKey = side === 'left' ? 'design' : 'technology'
    panelReadiness.value[panelKey].contentReady = ready
  }
  
  function requestPanelEntrance(side: 'left' | 'right') {
    if (currentState.value === 'panel-open' && activePanelSide.value === side) {
      return
    }
    
    if (currentState.value === 'panel-open' && activePanelSide.value !== side) {
      switchToPanel(side)
      return
    }
    
    pendingPanelEntrance.value = side
    
    if (currentState.value === 'ready') {
      const panelKey = side === 'left' ? 'design' : 'technology'
      if (panelReadiness.value[panelKey].dataLoaded) {
        enterPanelImmediately(side)
      } else {
        // Wait for panel data
        transitionTo('loading-for-panel', `waiting for ${side} panel data`)
      }
    } else if (currentState.value === 'initializing') {
      transitionTo('loading-for-panel', `preparing for ${side} panel entrance`)
    } else if (currentState.value === 'panel-transitioning') {
      pendingPanelEntrance.value = side
    }
  }
  
  function enterPanelImmediately(side: 'left' | 'right') {
    transitionTo('panel-transitioning', `entering ${side} panel`)
    activePanelSide.value = side
    pendingPanelEntrance.value = null
    
    setTimeout(() => {
      if (currentState.value === 'panel-transitioning') {
        transitionTo('panel-open', `${side} panel opened`)
      }
    }, 300) // GSAP animation time (250ms + 50ms buffer)
  }
  
  function exitPanel() {
    if (currentState.value === 'panel-open') {
      transitionTo('panel-transitioning', 'closing panel')
      activePanelSide.value = null
      
      setTimeout(() => {
        if (currentState.value === 'panel-transitioning') {
          transitionTo('ready', 'panel closed')
        }
      }, 300) // GSAP animation time (250ms + 50ms buffer)
    }
  }

  function switchToPanel(targetSide: 'left' | 'right') {
    const panelKey = targetSide === 'left' ? 'design' : 'technology'
    
    if (panelReadiness.value[panelKey].dataLoaded) {
      transitionTo('panel-transitioning', `switching from ${activePanelSide.value} to ${targetSide}`)
      activePanelSide.value = targetSide
      
      setTimeout(() => {
        if (currentState.value === 'panel-transitioning') {
          transitionTo('panel-open', `${targetSide} panel opened`)
        }
      }, 400)
    } else {
      transitionTo('loading-for-panel', `loading ${targetSide} panel for switch`)
      pendingPanelEntrance.value = targetSide
    }
  }
  
  function checkReadinessAndTransition() {
    if (!isReady.value) return
    
    if (currentState.value === 'initializing') {
      isInitialLoad.value = false
      
      if (pendingPanelEntrance.value) {

        const side = pendingPanelEntrance.value
        const panelKey = side === 'left' ? 'design' : 'technology'
        
        if (panelReadiness.value[panelKey].dataLoaded) {
          handlePanelDataReady(side)
        } else {
          transitionTo('loading-for-panel', `waiting for ${side} panel data`)
        }
      } else {
        transitionTo('ready', 'initialization complete')
      }
    }
  }
  
  function handlePanelDataReady(side: 'left' | 'right') {
    if (pendingPanelEntrance.value !== side) return
    
    if (shouldDelayPanelEntrance.value && isInitialLoad.value) {
      transitionTo('ready', 'showing homepage briefly')
      setTimeout(() => {
        if (pendingPanelEntrance.value === side) {
          enterPanelImmediately(side)
        }
      }, panelEntranceGracePeriod.value)
    } else {
      if (isInitialLoad.value) {
        // Reduced delay for GSAP - faster and smoother entrance
        setTimeout(() => enterPanelImmediately(side), 1000)
      } else {
        enterPanelImmediately(side)
      }
    }
  }
  
  // Configuration
  function setGracePeriod(ms: number) {
    panelEntranceGracePeriod.value = ms
  }
  
  function setShouldDelayEntrance(delay: boolean) {
    shouldDelayPanelEntrance.value = delay
  }
  
  function reset() {
    pendingPanelEntrance.value = null
    activePanelSide.value = null
    if (currentState.value !== 'initializing') {
      transitionTo('ready', 'reset')
    }
  }

  function shouldLoadingWaitForPanel(): boolean {
    if (!pendingPanelEntrance.value) return false
    
    const side = pendingPanelEntrance.value
    const panelKey = side === 'left' ? 'design' : 'technology'
    return !panelReadiness.value[panelKey].dataLoaded
  }
  
  // Animation coordination methods
  function startEntryAnimationSequence() {
    // Start panel animation (existing timing preserved)
    setTimeout(() => {
      // This will be called by triggerInitialPanelAnimation in App.vue
    }, ANIMATION_TIMING.LOGO_COMPLETE_DELAY)
    
    // Start header fade-in while loading is fading out
    setTimeout(() => {
      animationStates.value.headerEntering = true
    }, ANIMATION_TIMING.HEADER_ENTRANCE_DELAY)
    
    // Start footer animation after content appears  
    setTimeout(() => {
      animationStates.value.footerEntering = true
    }, ANIMATION_TIMING.FOOTER_ENTRANCE_DELAY)
  }
  
  function setLoadingHidden(hidden: boolean) {
    animationStates.value.loadingHidden = hidden
  }
  
  function resetAnimationStates() {
    animationStates.value.headerEntering = false
    animationStates.value.footerEntering = false
    animationStates.value.loadingHidden = false
  }
  
  return {
    // State
    currentState,
    previousState,
    activePanelSide,
    pendingPanelEntrance,
    panelReadiness,
    animationStates,
    
    // Computed
    isReady,
    canEnterPanel,
    
    // Actions
    transitionTo,
    setCoreDataLoaded,
    setAnimationCompleted,
    setPanelDataLoaded,
    setPanelContentReady,
    requestPanelEntrance,
    exitPanel,
    switchToPanel,
    setGracePeriod,
    setShouldDelayEntrance,
    reset,
    shouldLoadingWaitForPanel,
    
    // Animation coordination
    startEntryAnimationSequence,
    setLoadingHidden,
    resetAnimationStates,
    
    // Route utilities
    RouteUtils,
    
    // Internal (for debugging)
    checkReadinessAndTransition
  }
}) 